import type { CascadeEffect } from '../../../data/full/jongleur';
import { distanceSquared } from '../../../shared/vec';
import { hurtEnemy } from '../../effects';
import type { SkillModule } from '../../skills/types';
import type { EnemyState, PropState, SimState } from '../../state';
import type { StepContext } from '../../systems/types';
import { CLUB, clubsOfPlayer } from '../attacks/clubs';
import { ownerOf } from '../../props';

export const cascade: SkillModule<CascadeEffect> = {
  cast({ state }, player, effect, _input, power) {
    for (const club of clubsOfPlayer(state, player)) {
      club.phase = 'bouncing';
      club.x = club.prevX = player.x;
      club.y = club.prevY = player.y;
      club.vx = club.vy = 0;
      club.damage = effect.damage * power;
      club.hitsLeft = effect.bounces;
      club.ticksLeft = hopTicks(effect);
      club.hitIds = [];
      delete club.targetId;
    }
  },

  update(ctx) {
    const { state } = ctx;
    for (const club of state.props ?? []) {
      if (club.kind === CLUB && club.phase === 'bouncing') {
        bounce(ctx, club, effectOf(ctx, club));
      }
    }
  },
};

// A hop gives up on a bad vibe it cannot catch within twice the reach.
function hopTicks(effect: CascadeEffect): number {
  return Math.ceil((2 * effect.range) / effect.speed);
}

function effectOf(ctx: StepContext, club: PropState): CascadeEffect {
  const owner = ownerOf(ctx.state, club);
  const effect = ctx.content.classes.get(owner.classId)?.skill.effect;
  if (effect?.kind !== 'cascade') {
    throw new Error(`player ${String(owner.id)} of class "${owner.classId}" has no cascade`);
  }
  return effect;
}

function bounce(ctx: StepContext, club: PropState, effect: CascadeEffect): void {
  const { state } = ctx;
  let target = state.enemies.find((enemy) => enemy.id === club.targetId && enemy.hp > 0);
  target ??= nextTarget(state, club, effect.range);
  club.ticksLeft -= 1;
  if (target === undefined || club.ticksLeft <= 0) {
    returnHome(club);
    return;
  }
  club.targetId = target.id;
  const dx = target.x - club.x;
  const dy = target.y - club.y;
  const distance = Math.sqrt(dx * dx + dy * dy);
  if (distance > effect.speed + target.radius) {
    club.vx = (dx / distance) * effect.speed;
    club.vy = (dy / distance) * effect.speed;
    club.x += club.vx;
    club.y += club.vy;
    return;
  }
  club.x = target.x;
  club.y = target.y;
  hurtEnemy(state, target, club.damage, club.playerId, {
    x: club.prevX,
    y: club.prevY,
  });
  (club.hitIds ??= []).push(target.id);
  club.hitsLeft -= 1;
  club.ticksLeft = hopTicks(effect);
  delete club.targetId;
  if (club.hitsLeft <= 0) {
    returnHome(club);
  }
}

function nextTarget(state: SimState, club: PropState, range: number): EnemyState | undefined {
  const taken = new Set<number>();
  for (const other of state.props ?? []) {
    if (other !== club && other.playerId === club.playerId && other.targetId !== undefined) {
      taken.add(other.targetId);
    }
  }
  let free: EnemyState | undefined;
  let any: EnemyState | undefined;
  let freeDistance = range * range;
  let anyDistance = range * range;
  for (const enemy of state.enemies) {
    if (enemy.hp <= 0 || club.hitIds?.includes(enemy.id) === true) {
      continue;
    }
    const distance = distanceSquared(enemy, club);
    if (distance <= anyDistance) {
      any = enemy;
      anyDistance = distance;
    }
    if (!taken.has(enemy.id) && distance <= freeDistance) {
      free = enemy;
      freeDistance = distance;
    }
  }
  return free ?? any;
}

function returnHome(club: PropState): void {
  club.phase = 'returning';
  delete club.targetId;
  delete club.hitIds;
}
